import { useEffect, useState, useCallback, useRef } from 'react';
import { characterApi, Character } from '@/api/client';
import { useCharacterStore } from '@/store/characterStore';
import { useAuthStore } from '@/store/authStore';
import { useLocalization } from '@/i18n';

const LOCAL_CHARACTERS_KEY = 'pf2e_local_characters';

function getLocalCharacterIds(): string[] {
  try {
    const stored = localStorage.getItem(LOCAL_CHARACTERS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function addLocalCharacterId(id: string): void {
  try {
    const ids = getLocalCharacterIds();
    if (!ids.includes(id)) {
      ids.push(id);
      localStorage.setItem(LOCAL_CHARACTERS_KEY, JSON.stringify(ids));
    }
  } catch (e) {
    console.error('Failed to save local character ID:', e);
  }
}

function removeLocalCharacterId(id: string): void {
  try {
    const ids = getLocalCharacterIds().filter(cid => cid !== id);
    localStorage.setItem(LOCAL_CHARACTERS_KEY, JSON.stringify(ids));
  } catch (e) {
    console.error('Failed to remove local character ID:', e);
  }
}

export { getLocalCharacterIds, addLocalCharacterId, removeLocalCharacterId };

async function loadAllCharacters(locale: string): Promise<Character[]> {
  let serverCharacters: Character[] = [];
  try {
    const response = await characterApi.getAllLocalized(locale);
    serverCharacters = response.data.characters || [];
  } catch (err) {
    if ((err as any)?.response?.status !== 401) {
      throw err;
    }
    // 401 is normal for unauthenticated users — proceed to local characters
  }

  const localIds = getLocalCharacterIds();
  const localCharacters: Character[] = [];
  for (const id of localIds) {
    try {
      const response = await characterApi.getByIdLocalized(id, locale);
      localCharacters.push(response.data);
    } catch {
      removeLocalCharacterId(id);
    }
  }

  return [...serverCharacters, ...localCharacters];
}

/**
 * Called ONCE at the app level (AppWithLocation in App.tsx).
 * Manages fetching characters on startup and on auth state changes.
 * Never remounts because AppWithLocation never remounts.
 */
export function useCharactersInit() {
  const { setCharacters, setBootstrapped, setError } = useCharacterStore();
  const { language, t } = useLocalization();
  const isFetchingRef = useRef(false);
  const lastLocaleRef = useRef<string | null>(null);

  const fetchCharacters = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const allCharacters = await loadAllCharacters(language);

      const current = useCharacterStore.getState().characters;
      const localeChanged = lastLocaleRef.current !== language;
      const dataChanged =
        localeChanged ||
        allCharacters.length !== current.length ||
        allCharacters.some((c, i) => c.id !== current[i]?.id || c.updated_at !== current[i]?.updated_at);

      if (dataChanged || !useCharacterStore.getState().hasBootstrapped) {
        // Only update if data changed or if this is the first successful load
        if (allCharacters.length > 0 || current.length === 0) {
          setCharacters(allCharacters);
        }
        setError(null);
      }
      lastLocaleRef.current = language;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('character.errors.fetchList', {}, "Failed to fetch characters."));
    } finally {
      setBootstrapped();
      isFetchingRef.current = false;
    }
  }, [language, setCharacters, setBootstrapped, setError, t]);

  useEffect(() => {
    // Initial load
    fetchCharacters();

    // Re-fetch on auth change (login / logout)
    const unsubscribe = useAuthStore.subscribe((state, prevState) => {
      if (prevState.isAuthenticated !== state.isAuthenticated) {
        fetchCharacters();
      }
    });
    return unsubscribe;
  }, [fetchCharacters]);
}

/**
 * Lightweight read-only hook for components that need the character list.
 * No fetching — data comes from the store populated by useCharactersInit.
 */
export function useCharacters() {
  const characters = useCharacterStore(state => state.characters);
  const hasBootstrapped = useCharacterStore(state => state.hasBootstrapped);
  const error = useCharacterStore(state => state.error);
  return { characters, isLoading: !hasBootstrapped, error };
}

export function useCharacter(id?: string) {
  const store = useCharacterStore();
  const { language, t } = useLocalization();
  const locale = language;
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!id) return;
    // Read from store directly to avoid stale closure on `store` reference
    const existing = useCharacterStore.getState().characters.find((c) => c.id === id);
    if (existing) return;

    setIsFetching(true);
    characterApi.getByIdLocalized(id, locale)
      .then((response) => useCharacterStore.getState().setCurrentCharacter(response.data))
      .catch((err) => setLocalError(err instanceof Error ? err.message : t('character.errors.fetchOne', {}, "Failed to fetch character.")))
      .finally(() => setIsFetching(false));
  }, [id, locale, t]);

  const createCharacter = async (data: Omit<Character, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    try {
      setLocalLoading(true);
      const response = await characterApi.createLocalized(data, locale);
      const character = response.data;
      if (!character.user_id) {
        addLocalCharacterId(character.id);
      }
      store.addCharacter(character);
      return character;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('character.errors.create', {}, "Failed to create character.");
      setLocalError(errorMsg);
      throw err;
    } finally {
      setLocalLoading(false);
    }
  };

  const update = async (characterId: string, data: Partial<Character>) => {
    try {
      setLocalLoading(true);
      const response = await characterApi.updateLocalized(characterId, data, locale);
      const updated = response.data;
      store.updateCharacter(updated);
      return updated;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('character.errors.update', {}, "Failed to update character.");
      setLocalError(errorMsg);
      throw err;
    } finally {
      setLocalLoading(false);
    }
  };

  const remove = async (characterId: string) => {
    try {
      setLocalLoading(true);
      await characterApi.delete(characterId);
      store.removeCharacter(characterId);
      removeLocalCharacterId(characterId);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('character.errors.delete', {}, "Failed to delete character.");
      setLocalError(errorMsg);
      throw err;
    } finally {
      setLocalLoading(false);
    }
  };

  const character = id
    ? store.characters.find((c: Character) => c.id === id) || store.currentCharacter
    : store.currentCharacter;

  return {
    character,
    createCharacter,
    update,
    remove,
    isLoading: localLoading || isFetching,
    error: localError
  };
}
